/*
 * TrialEmailGate — pedido de email in-chat (trial v2, anônimo dia 2-3).
 *
 * Mensagem local estilo Dr. Pera (template i18n) + inputs INLINE dentro do
 * chat: email → código 6 dígitos → JWT (mesmo mecanismo do useAuth).
 *
 * SEGURANÇA: email e código JAMAIS passam por /api/ai/chat (jamais chegam
 * ao LLM). Este componente captura os valores e os envia SÓ via callbacks
 * que batem em /api/auth/request-code e /api/auth/verify-code.
 */
import React, { useState, useRef, useEffect } from 'react';
import { GoogleLoginButton } from './GoogleLoginButton';
import type { ChatError } from '../types';

interface TrialEmailGateProps {
  /** POST /api/auth/request-code (origin web-trial) */
  onRequestCode: (email: string) => Promise<void>;
  /** POST /api/auth/verify-code com anonId (migra memória server-side) */
  onVerifyCode: (email: string, code: string) => Promise<void>;
  /** Locale da UI — repassado pro chip Google (opcional, default pt-BR) */
  locale?: string;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Validação de formato de email (frontend — backend revalida) */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Phase = 'email' | 'code';

/** Mapeia erro lançado pelos endpoints auth → chave i18n amigável */
function errorToKey(err: unknown, phase: Phase): string {
  // Rate limit 429 vem do interceptor global como fair_use_exceeded
  if (err && typeof err === 'object' && 'type' in err) {
    const type = (err as ChatError).type;
    if (type === 'fair_use_exceeded') return 'trial.email.errorRateLimit';
    if (type === 'network') return 'trial.email.errorGeneric';
  }
  return phase === 'code' ? 'trial.email.errorCode' : 'trial.email.errorGeneric';
}

export function TrialEmailGate({ onRequestCode, onVerifyCode, locale, t }: TrialEmailGateProps) {
  const [phase, setPhase] = useState<Phase>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Foco automático no input da fase ativa
  useEffect(() => {
    if (phase === 'email') emailInputRef.current?.focus();
    else codeInputRef.current?.focus();
  }, [phase]);

  const handleRequestCode = async () => {
    const emailTrim = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(emailTrim)) {
      setErrorKey('trial.email.errorInvalidEmail');
      return;
    }
    setBusy(true);
    setErrorKey(null);
    try {
      await onRequestCode(emailTrim);
      setEmail(emailTrim);
      setPhase('code');
    } catch (err) {
      setErrorKey(errorToKey(err, 'email'));
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    const codeTrim = code.trim();
    if (!/^\d{6}$/.test(codeTrim)) {
      setErrorKey('trial.email.errorCode');
      return;
    }
    setBusy(true);
    setErrorKey(null);
    try {
      await onVerifyCode(email, codeTrim);
      // Sucesso: useAuth ganha JWT → App re-renderiza logado. Nada a fazer.
    } catch (err) {
      setErrorKey(errorToKey(err, 'code'));
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    background: 'var(--ink)',
    border: `1px solid ${errorKey ? 'rgba(255,106,0,0.6)' : 'var(--ink-mute)'}`,
    color: 'var(--ink-light)',
    fontFamily: 'var(--font-sans)',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 16px',
    background: busy ? 'var(--ink-soft)' : 'var(--cta-blue)',
    border: 'none',
    color: busy ? 'var(--ink-mute)' : '#FFFFFF',
    fontFamily: 'var(--font-sans)',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.03em',
    cursor: busy ? 'wait' : 'pointer',
    flexShrink: 0,
  };

  return (
    <div
      style={{
        padding: '10px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
      }}
    >
      {/* Bolha local estilo Dr. Pera */}
      <div
        style={{
          maxWidth: '80%',
          padding: '10px 14px',
          border: '1px solid rgba(124,124,248,0.30)',
          color: 'var(--ink-light)',
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          lineHeight: 1.7,
          whiteSpace: 'pre-wrap',
        }}
      >
        {phase === 'email' ? t('trial.email.ask') : t('trial.email.sent')}
      </div>

      {/* Inputs inline — NUNCA passam pelo endpoint de chat */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          width: '100%',
          maxWidth: '420px',
          alignItems: 'center',
        }}
      >
        {phase === 'email' ? (
          <>
            <input
              ref={emailInputRef}
              type="email"
              value={email}
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRequestCode();
              }}
              placeholder={t('trial.email.emailPlaceholder')}
              aria-label={t('trial.email.emailPlaceholder')}
              autoComplete="email"
              style={inputStyle}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleRequestCode()}
              style={buttonStyle}
            >
              {t('trial.email.sendCta')}
            </button>
          </>
        ) : (
          <>
            <input
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              disabled={busy}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleVerify();
              }}
              placeholder={t('trial.email.codePlaceholder')}
              aria-label={t('trial.email.codePlaceholder')}
              autoComplete="one-time-code"
              style={{ ...inputStyle, letterSpacing: '0.3em', maxWidth: '140px' }}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleVerify()}
              style={buttonStyle}
            >
              {t('trial.email.verifyCta')}
            </button>
          </>
        )}
      </div>

      {/* Chip "Continuar com Google" — só aparece se o backend está
          configurado (GoogleLoginButton consulta /status e renderiza null
          se dormante). Redirect full-page: email/tokens JAMAIS passam
          pelo endpoint de chat. */}
      {phase === 'email' && (
        <div style={{ width: '100%', maxWidth: '420px' }}>
          <GoogleLoginButton locale={locale ?? 'pt-BR'} t={t} />
        </div>
      )}

      {/* Fase código: link pra corrigir o email */}
      {phase === 'code' && !busy && (
        <button
          type="button"
          onClick={() => {
            setPhase('email');
            setCode('');
            setErrorKey(null);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-mute)',
            fontFamily: 'var(--font-sans)',
            fontSize: '12px',
            cursor: 'pointer',
            padding: '0 4px',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          {t('trial.email.changeEmail')}
        </button>
      )}

      {/* Erro amigável — reintento sempre possível */}
      {errorKey !== null && (
        <div
          role="alert"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            color: '#FF6A00',
            lineHeight: 1.5,
          }}
        >
          ⚠ {t(errorKey)}
        </div>
      )}
    </div>
  );
}
